import React, { useState } from 'react';
import { auth } from '../firebase';
import { updateUserProfile } from '../utils/secureFunctions';
import { countries, citiesByCountry } from '../data/countries';

interface Props {
  isOpen: boolean;
  defaultName?: string;
  onClose: () => void;
}

const ProfileCompletionModal: React.FC<Props> = ({ isOpen, defaultName, onClose }) => {
  const displayName = defaultName || auth.currentUser?.displayName || '';
  const [country, setCountry] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [locationFreeText, setLocationFreeText] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [dob, setDob] = useState<string>('');
  const [dobYear, setDobYear] = useState<string>('');
  const [dobMonth, setDobMonth] = useState<string>('');
  const [dobDay, setDobDay] = useState<string>('');
  const pad2 = (n: number) => n.toString().padStart(2, '0');
  const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();
  const recomputeDOB = (y?: string, m?: string, d?: string) => {
    const year = (y ?? dobYear) || '';
    const month = (m ?? dobMonth) || '';
    const day = (d ?? dobDay) || '';
    const full = year && month && day ? `${year}-${month}-${day}` : '';
    setDob(full);
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const canSave = ((city && country) || locationFreeText.trim().length >= 2) && gender && dob;

  const handleSave = async () => {
    if (!auth.currentUser) return;
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const finalLocation = city && country ? `${city}, ${countries.find(c => c.value === country)?.label || country}` : locationFreeText.trim();
      await updateUserProfile({
        location: finalLocation,
        gender,
        dateOfBirth: dob,
        profilePrivacy: { showLocation: true, showAge: false, showGender: false },
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.backdrop}>
      <div style={styles.modal}>
        <h2 style={{ marginTop: 0, color: '#072B2C' }}>Complete your profile</h2>
        <p style={{ marginTop: 0, color: '#5B6E6E' }}>Please provide these details to continue using Belivah.</p>

        {/* Name field removed to avoid duplication; we keep existing displayName */}
        {displayName && (
          <p style={{ marginTop: 0, color: '#5B6E6E' }}>Signed in as <strong>{displayName}</strong>.</p>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>Country</label>
          <select
            style={{ ...styles.input, height: 42 }}
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setCity(''); // Reset city when country changes
            }}
          >
            <option value="">Select Country</option>
            {countries.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        {country && citiesByCountry[country] && (
          <div style={styles.formGroup}>
            <label style={styles.label}>City</label>
            <select
              style={{ ...styles.input, height: 42 }}
              value={city}
              onChange={(e) => setCity(e.target.value)}
            >
              <option value="">Select City</option>
              {citiesByCountry[country].map(cityName => <option key={cityName} value={cityName}>{cityName}</option>)}
            </select>
          </div>
        )}

        {(!country || country === 'Other' || !citiesByCountry[country]) && (
          <div style={styles.formGroup}>
            <label style={styles.label}>Location (free text)</label>
            <input style={styles.input} type="text" value={locationFreeText} onChange={(e) => setLocationFreeText(e.target.value)} placeholder="City, Country" />
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label}>Sex</label>
          <select style={{ ...styles.input, height: 42 }} value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Date of Birth</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              style={{ ...styles.input, height: 42 }}
              value={dobDay}
              onChange={(e) => { setDobDay(e.target.value); recomputeDOB(undefined, undefined, e.target.value); }}
            >
              <option value="">Day</option>
              {(() => {
                const y = parseInt(dobYear || '2000', 10);
                const m = parseInt(dobMonth || '01', 10);
                const max = daysInMonth(isNaN(y) ? 2000 : y, isNaN(m) ? 1 : m);
                return Array.from({ length: max }, (_, i) => pad2(i + 1)).map(d => (
                  <option key={d} value={d}>{d}</option>
                ));
              })()}
            </select>
            <select
              style={{ ...styles.input, height: 42 }}
              value={dobMonth}
              onChange={(e) => { setDobMonth(e.target.value); recomputeDOB(undefined, e.target.value, undefined); }}
            >
              <option value="">Month</option>
              {Array.from({ length: 12 }, (_, i) => pad2(i + 1)).map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <select
              style={{ ...styles.input, height: 42 }}
              value={dobYear}
              onChange={(e) => { setDobYear(e.target.value); recomputeDOB(e.target.value, undefined, undefined); }}
            >
              <option value="">Year</option>
              {(() => {
                const current = new Date().getFullYear();
                return Array.from({ length: current - 1900 + 1 }, (_, i) => String(current - i)).map(y => (
                  <option key={y} value={y}>{y}</option>
                ));
              })()}
            </select>
          </div>
        </div>

        {error && <div style={{ color: '#b00020', marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button style={styles.secondaryBtn} onClick={handleSave} disabled={!canSave || saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 480,
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 12px 40px rgba(0,0,0,0.25)'
  },
  formGroup: {
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 14,
    color: '#345',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    height: 38,
    borderRadius: 8,
    border: '1px solid #cfd8dc',
    padding: '8px 12px',
    fontSize: 14,
  },
  secondaryBtn: {
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 16px',
    cursor: 'pointer',
  }
};

export default ProfileCompletionModal;
